import React, { useContext, useEffect, useMemo, useState } from "react";
import { FiArrowRight, FiBriefcase, FiCheck, FiClock, FiUser } from "react-icons/fi";
import { Link } from "react-router-dom";
import ClientAuthContext from "../../context/ClientAuthContext";
import { serverInstance } from "../../service/axios";
import "./PricingTable.scss";

const DEFAULT_COUPON_VALIDATION_MEETING_ID = "1775628421050";

const getAuthHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

const getNumber = (...values) => {
  for (const value of values) {
    const numberValue = Number(value);
    if (Number.isFinite(numberValue)) {
      return numberValue;
    }
  }
  return null;
};

const PricingTable = ({ data, language }) => {

  const { userData } = useContext(ClientAuthContext) || {};
  const [plans, setPlans] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponMessageType, setCouponMessageType] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const normalizedCouponCode = useMemo(() => couponCode.trim().toUpperCase(), [couponCode]);
  const canUseCoupon = !!userData?.id;
  const membershipEndDate = userData?.paid_until ? new Date(userData.paid_until) : null;
  const hasMembershipEndDate = membershipEndDate && !Number.isNaN(membershipEndDate.getTime());
  const membershipActive = hasMembershipEndDate && membershipEndDate > new Date();
  const formattedMembershipEndDate = hasMembershipEndDate
    ? membershipEndDate.toLocaleString('mn-MN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : "";

  useEffect(() => {
    serverInstance.get("/app/plans")
      .then((res) => {
        if (res.status === 200) {
          setPlans(res.data.items);
        }
      })
      .catch((err) => {
        console.log(err);
      })
    
    window.scrollTo(0, 0);
  }, []);

  const formatPrice = (price) => (
    new Intl.NumberFormat('mn-MN', { style: 'decimal', maximumFractionDigits: 0 }).format(price)
  );

  const getPlanTypeLabel = (type) => (
    type === "doctor" ? data.person : data.company
  );

  const getCouponText = (key, englishFallback, mongolianFallback) => (
    data?.[key] || (language ? englishFallback : mongolianFallback)
  );

  const normalizePlanPrices = (responseData) => {
    const priceRows = responseData?.plan_prices || responseData?.prices || responseData?.items || [];
    if (!Array.isArray(priceRows)) {
      return {};
    }

    return priceRows.reduce((acc, item) => {
      const planId = String(item?.plan_id || item?.id || item?.planId || "");
      if (!planId) {
        return acc;
      }

      const amount = getNumber(item.amount, item.price, item.discounted_price, item.discountedPrice);
      const originalAmount = getNumber(item.original_amount, item.original_price, item.originalPrice);
      const discountAmount = getNumber(item.coupon_discount_amount, item.discount_amount, item.discountAmount);

      acc[planId] = {
        amount,
        originalAmount,
        discountAmount,
      };
      return acc;
    }, {});
  };

  const getCouponPayload = () => ({
    coupon_code: normalizedCouponCode,
    product: "membership",
    plan_id: plans[0]?.id,
    plan_ids: plans.map((plan) => plan.id),
  });

  const validateCouponWithFallback = (token) => (
    serverInstance.post("/client/invoice/membership/coupon", getCouponPayload(), {
      headers: getAuthHeaders(token),
    })
      .catch((err) => {
        if (![404, 405].includes(err?.response?.status)) {
          throw err;
        }

        return serverInstance.post("/client/invoice/meeting/coupon", {
          coupon_code: normalizedCouponCode,
          meeting_id: DEFAULT_COUPON_VALIDATION_MEETING_ID,
        }, {
          headers: getAuthHeaders(token),
        });
      })
  );

  const getPlanPricing = (plan) => {
    const originalAmount = Number(plan.price);
    const planCouponPrice = appliedCoupon?.planPrices?.[String(plan.id)];
    const planDiscountAmount = Number(planCouponPrice?.discountAmount);
    const planDiscountedAmount = Number(planCouponPrice?.amount);

    if (Number.isFinite(planDiscountedAmount) && planDiscountedAmount >= 0) {
      return {
        amount: planDiscountedAmount,
        originalAmount,
        discountAmount: Math.max(0, originalAmount - planDiscountedAmount),
      };
    }

    if (Number.isFinite(planDiscountAmount) && planDiscountAmount > 0) {
      return {
        amount: Math.max(0, originalAmount - planDiscountAmount),
        originalAmount,
        discountAmount: Math.min(originalAmount, planDiscountAmount),
      };
    }

    const couponPercent = Number(appliedCoupon?.percent);
    const couponAmount = Number(appliedCoupon?.amount);
    const percentDiscountAmount = Number.isFinite(couponPercent) && couponPercent > 0
      ? Math.round(originalAmount * Math.min(couponPercent, 100) / 100)
      : 0;
    const discountAmount = Math.max(0, Math.min(
      originalAmount,
      Number.isFinite(couponAmount) && couponAmount > 0 ? couponAmount : percentDiscountAmount
    ));

    return {
      amount: Math.max(0, originalAmount - discountAmount),
      originalAmount,
      discountAmount,
    };
  };

  const handleCouponChange = (e) => {
    setCouponCode(e.target.value.toUpperCase());
    setAppliedCoupon(null);
    setCouponMessage("");
    setCouponMessageType("");
  };

  const applyCouponCode = () => {
    if (!normalizedCouponCode) {
      setAppliedCoupon(null);
      setCouponMessageType("error");
      setCouponMessage(getCouponText("invalidCouponCode", "Invalid coupon code", "Купон код буруу байна"));
      return;
    }

    const token = localStorage.getItem("msidclienttoken");
    if (!token || !userData?.id) {
      setAppliedCoupon(null);
      setCouponMessageType("error");
      setCouponMessage(getCouponText("couponLoginRequired", "Please sign in to use coupon", "Купон ашиглахын тулд нэвтэрнэ үү"));
      return;
    }

    setApplyingCoupon(true);
    setCouponMessage("");
    setCouponMessageType("");

    validateCouponWithFallback(token)
      .then((res) => {
        const responseData = res?.data || {};
        const appliedCode = String(responseData.coupon_code || normalizedCouponCode).trim().toUpperCase();
        const couponPercent = getNumber(responseData.coupon_percent, responseData.percent, responseData.discount_percent);
        const couponAmount = getNumber(responseData.coupon_amount, responseData.coupon_discount_amount, responseData.discount_amount);
        const planPrices = normalizePlanPrices(responseData);
        const hasDiscount = (couponPercent && couponPercent > 0) ||
          (couponAmount && couponAmount > 0) ||
          Object.entries(planPrices).some(([planId, item]) => {
            const plan = plans.find((planItem) => String(planItem.id) === String(planId));
            const originalAmount = Number(item.originalAmount ?? plan?.price);
            const amount = Number(item.amount);
            return (Number.isFinite(amount) && Number.isFinite(originalAmount) && amount < originalAmount) ||
              Number(item.discountAmount) > 0;
          });

        if (!hasDiscount) {
          setAppliedCoupon(null);
          setCouponMessageType("error");
          setCouponMessage(getCouponText("invalidCouponCode", "Invalid coupon code", "Купон код буруу байна"));
          return;
        }

        setCouponCode(appliedCode);
        setAppliedCoupon({
          code: appliedCode,
          percent: couponPercent || 0,
          amount: couponAmount || 0,
          planPrices,
        });
        setCouponMessageType("success");
        setCouponMessage(getCouponText("couponApplied", "Successfully applied code", "Купон код амжилттай"));
      })
      .catch((err) => {
        const responseData = err?.response?.data;
        const responseMessage = String(responseData?.message || (typeof responseData === "string" ? responseData : "")).toLowerCase();
        setAppliedCoupon(null);
        setCouponMessageType("error");
        if (err?.response?.status === 401) {
          setCouponMessage(getCouponText("couponLoginRequired", "Please sign in to use coupon", "Купон ашиглахын тулд нэвтэрнэ үү"));
        }
        else if (responseMessage.includes("limit")) {
          setCouponMessage(getCouponText("couponLimitReached", "Coupon code limit reached", "Купон кодын хязгаар дууссан байна"));
        }
        else {
          setCouponMessage(getCouponText("invalidCouponCode", "Invalid coupon code", "Купон код буруу байна"));
        }
      })
      .finally(() => setApplyingCoupon(false));
  };

  const getPaymentLink = (planId) => ({
    pathname: `/client/payment/${planId}`,
    search: appliedCoupon?.code ? `?coupon_code=${encodeURIComponent(appliedCoupon.code)}` : "",
  });

  const getPaymentState = (plan) => {
    if (!appliedCoupon?.code) {
      return undefined;
    }

    const pricing = getPlanPricing(plan);
    return {
      couponCode: appliedCoupon.code,
      couponPercent: appliedCoupon.percent,
      couponAmount: appliedCoupon.amount,
      couponDiscountAmount: pricing.discountAmount,
    };
  };

  return (
    <div className="pricing-table">
      <div className="title">
        <h2>{data.title}</h2>
      </div>
      {hasMembershipEndDate ? (
        <div className={`membership-status ${membershipActive ? "active" : "expired"}`}>
          <span>{membershipActive ? data.currentMembershipEndDate : data.expiredMembershipEndDate}</span>
          <strong>{formattedMembershipEndDate}</strong>
        </div>
      ) : null}
      <div className="pricing-content">
        <section className="shared-benefits" aria-labelledby="membership-benefits-title">
          <h3 id="membership-benefits-title">{data.featureTitle}</h3>
          <ul>
            {data.features?.map((benefit, index) => (
              <li key={index}>
                <span className="benefit-icon" aria-hidden="true">
                  <FiCheck />
                </span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </section>

        {canUseCoupon ? (
          <section className="membership-coupon" aria-label={data.couponCode || (language ? "Coupon code" : "Купон код")}>
            <label htmlFor="membership-coupon">{data.couponCode || (language ? "Coupon code" : "Купон код")}</label>
            <div className="coupon-input-row">
              <input
                id="membership-coupon"
                type="text"
                name="coupon_code"
                value={couponCode}
                onChange={handleCouponChange}
                autoCapitalize="characters"
              />
              <button
                type="button"
                className="coupon-apply-button"
                disabled={applyingCoupon || !normalizedCouponCode || normalizedCouponCode === appliedCoupon?.code}
                onClick={applyCouponCode}
              >
                {applyingCoupon ? (data.loading || (language ? "Please wait..." : "Түр хүлээнэ үү...")) : (data.applyCoupon || (language ? "Apply" : "Хэрэглэх"))}
              </button>
            </div>
            {couponMessage ? (
              <div className={`coupon-message ${couponMessageType === "success" ? "coupon-success" : "coupon-error"}`}>
                {couponMessage}
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="plans" aria-label={data.title}>
          {plans.map((plan) => {
            const typeLabel = getPlanTypeLabel(plan.type);
            const termLabel = plan.term === 100 ? data.lifeLong : `${plan.term} ${data.year}`;
            const TypeIcon = plan.type === "doctor" ? FiUser : FiBriefcase;
            const pricing = getPlanPricing(plan);
            const hasCouponDiscount = Number(pricing.discountAmount) > 0 && pricing.amount < pricing.originalAmount;

            return (
              <article key={plan.id} className={`plan ${plan.type}`}>
                <div className="plan-header">
                  <div className="plan-type">
                    <span className="plan-type-icon" aria-hidden="true">
                      <TypeIcon />
                    </span>
                    <span>{typeLabel}</span>
                  </div>
                  <div className="plan-term">
                    <FiClock aria-hidden="true" />
                    <span>{termLabel}</span>
                  </div>
                </div>

                <div className={`plan-price ${hasCouponDiscount ? "discounted" : ""}`} aria-label={`${data.price}: ${formatPrice(pricing.amount)} ₮`}>
                  <span>{formatPrice(pricing.amount)}</span>
                  <small>₮</small>
                </div>
                {hasCouponDiscount ? (
                  <div className="plan-original-price">
                    {data.originalPrice || (language ? "Original price" : "Үндсэн үнэ")}: <span>{formatPrice(pricing.originalAmount)} ₮</span>
                  </div>
                ) : null}

                <Link
                  className="choose"
                  to={getPaymentLink(plan.id)}
                  state={getPaymentState(plan)}
                  aria-label={`${data.choose}: ${typeLabel}, ${termLabel}`}
                >
                  <span>{data.choose}</span>
                  <FiArrowRight aria-hidden="true" />
                </Link>
              </article>
            );
          })}
        </section>
      </div>
    </div>
  );
};

export default PricingTable;
